import { useCallback, useEffect, useState } from 'react'
import CustomerCombobox from '../CustomerCombobox'
import NewSupplierInvoiceDialog from './NewSupplierInvoiceDialog'
import SupplierInvoiceDetail, { StatusPill } from './SupplierInvoiceDetail'
import { allocatedNet, fetchSupplierInvoices, r2, type SupplierInvoice } from '../../lib/supplierInvoices'

interface Props { initialInvoiceId?: string | null }

export default function CostInvoicesTab({ initialInvoiceId = null }: Props) {
  const [rows, setRows] = useState<SupplierInvoice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState('')
  const [supplierId, setSupplierId] = useState('')
  const [invoiceNo, setInvoiceNo] = useState('')
  const [showNew, setShowNew] = useState(false)
  const [openId, setOpenId] = useState<string | null>(initialInvoiceId)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try { setRows(await fetchSupplierInvoices({ status, supplierId, invoiceNo })) }
    catch (e) { setError(e instanceof Error ? e.message : String(e)) }
    finally { setLoading(false) }
  }, [status, supplierId, invoiceNo])
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t) }, [load])

  const th = 'px-3 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400'
  const td = 'px-3 py-2 text-sm text-gray-800 dark:text-gray-200 whitespace-nowrap'
  const field = 'rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white'

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-64"><CustomerCombobox value={supplierId} onChange={setSupplierId} anyRole placeholder="All suppliers" /></div>
        <input className={field} placeholder="Invoice no…" value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} />
        <select className={field} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All statuses</option><option>UNALLOCATED</option><option>PARTIAL</option><option>ALLOCATED</option>
        </select>
        {(supplierId || status || invoiceNo) && <button className="text-sm text-blue-600 hover:underline" onClick={() => { setSupplierId(''); setStatus(''); setInvoiceNo('') }}>Clear</button>}
        <button onClick={() => setShowNew(true)} className="ml-auto rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">+ New supplier invoice</button>
      </div>

      {error && <div className="mb-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-900/40">
            <tr><th className={th}>Date</th><th className={th}>Supplier</th><th className={th}>Invoice no</th><th className={th}>Cur</th><th className={th}>Net</th><th className={th}>VAT</th><th className={th}>Total</th><th className={th}>Allocated / Unallocated (net)</th><th className={th}>Status</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading && <tr><td className={td} colSpan={9}>Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td className={td} colSpan={9}>No supplier invoices yet.</td></tr>}
            {rows.map(r => {
              const al = allocatedNet(r)
              return (
                <tr key={r.id} onClick={() => setOpenId(r.id)} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className={td}>{r.invoice_date}</td>
                  <td className={td}>{r.supplier?.name ?? '—'}</td>
                  <td className={td}>{r.invoice_no}
                    {(r.acks?.length ?? 0) > 0 && <span title={`${r.acks!.length} warning(s) were overridden -- open the invoice to see the log`} className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">⚠ {r.acks!.length}</span>}
                  </td>
                  <td className={td}>{r.currency}</td>
                  <td className={td}>{r.net_amount.toFixed(2)}</td>
                  <td className={td}>{r.vat_amount.toFixed(2)}</td>
                  <td className={td}>{r.total_amount.toFixed(2)}</td>
                  <td className={td}>{al.toFixed(2)} / {r2(r.net_amount - al).toFixed(2)}</td>
                  <td className={td}><StatusPill status={r.status} /></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {rows.length === 200 && <p className="mt-2 text-xs text-gray-500">Showing the latest 200 -- narrow with the filters above.</p>}

      {showNew && <NewSupplierInvoiceDialog onClose={() => setShowNew(false)} onCreated={id => { setShowNew(false); load(); setOpenId(id) }} />}
      {openId && <SupplierInvoiceDetail invoiceId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  )
}
